import type { SurpriseThreadView } from "./surprise";

/**
 * W4 SIGNALS (Agent Brains 13.5, B3, B15): the invisible-failure detector.
 *
 * An invisible failure is one every check of the system passed: all gates
 * green on the first pass (F-25), the owner approved without a substantive
 * edit (F-05), and still the outcome diverged from what the lead's own signals
 * promised. A priority-band lead, or one who asked for a consult, who then
 * ghosted or was lost. Nothing in the gate chain, the verifier or the owner's
 * review saw a fault, so only the outcome can say there was one. These are the
 * threads the learner mines first (design W4).
 *
 * Precision over recall: a thread with no decided draft proved nothing (the
 * two checks above are vacuous over no drafts), a missing band is not a priority band (U3 wrote no
 * row, so nothing was promised), a thread with any failed gate, retry or
 * substantive edit is a VISIBLE failure and out of scope here, and an open
 * thread has not diverged yet.
 *
 * Pure: no clock, no I/O, no model.
 */

export type InvisibleExpectation = "priority_band" | "consult_intent";

export interface InvisibleFailure {
  readonly threadId: string;
  readonly contactId: string;
  readonly outcome: "ghosted" | "lost";
  /** What the lead had promised, in a fixed order. */
  readonly against: readonly InvisibleExpectation[];
  readonly inquiryType: string;
  readonly closedAt: string;
}

/** The invisible failures among `views`, ordered by close time then thread id. */
export function detectInvisibleFailures(views: readonly SurpriseThreadView[]): InvisibleFailure[] {
  const out: InvisibleFailure[] = [];
  for (const v of views) {
    if (v.outcome !== "ghosted" && v.outcome !== "lost") continue;
    // "Every draft was green and approved" is vacuous for a thread with no decided draft (M4).
    if (v.decidedDraftCount <= 0) continue;
    if (!v.firstPassGatesGreen || !v.approvedWithoutEdit) continue;
    const against: InvisibleExpectation[] = [];
    if (v.band === "priority") against.push("priority_band");
    if (v.consultIntent) against.push("consult_intent");
    if (against.length === 0) continue;
    out.push({
      threadId: v.threadId,
      contactId: v.contactId,
      outcome: v.outcome,
      against,
      inquiryType: v.inquiryType,
      closedAt: v.closedAt,
    });
  }
  return out.sort((a, b) =>
    a.closedAt < b.closedAt ? -1 : a.closedAt > b.closedAt ? 1 : a.threadId < b.threadId ? -1 : a.threadId > b.threadId ? 1 : 0,
  );
}
